import {useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import Injector from '@s-ui/react-primitive-injector'
import DynamicRendering from '@adv-mt/mt-perf-dynamic-rendering'
import SharedCorporateFooter, {
  sharedCorporateFooterDesigns,
  sharedCorporateFooterHeights
} from '@adv-mt/mt-shared-corporate-footer'
import MotorSharedTopbar, {sharedTopbarDesigns} from '@adv-mt/mt-shared-topbar'

import {
  BACKGROUND_COLORS,
  FOOTER_BACKGROUND_COLORS_DICTIONARY,
  LAYOUT_BASIC_FULL_WIDTH_BASE_CLASS_NAME,
  MARGIN_TOP_SIZES,
  MAX_CONTENT_WIDTHS,
  SHARED_TOPBAR_MAX_CONTENT_WIDTHS_DICTIONARY
} from '../../settings.js'
import {provisos} from './settings.js'

const LayoutBasicFullWidth = ({
  bgColor = BACKGROUND_COLORS.WHITE,
  children,
  footerProps,
  isFooterless = false,
  isHeaderless = false,
  isHeaderFlush = false,
  linkFactory,
  maxContentWidth = MAX_CONTENT_WIDTHS.MEDIUM,
  topbarProps = {},
  className = ''
}) => {
  const {
    domain,
    browser: {deviceType}
  } = useContext(Context)
  const config = domain.get('config')

  const appName = config.get('APP_NAME')

  const showFooter = !isFooterless
  const showHeader = !isHeaderless

  const footerHeight = sharedCorporateFooterHeights[appName][deviceType]

  const baseClassName = cx(
    LAYOUT_BASIC_FULL_WIDTH_BASE_CLASS_NAME,
    {
      [`${LAYOUT_BASIC_FULL_WIDTH_BASE_CLASS_NAME}--headerless`]: !showHeader,
      [`${LAYOUT_BASIC_FULL_WIDTH_BASE_CLASS_NAME}--bg-${bgColor}`]: bgColor
    },
    className
  )

  return (
    <div className={baseClassName}>
      {showHeader && (
        <MotorSharedTopbar
          {...topbarProps}
          design={sharedTopbarDesigns.fullWidth}
          linkFactory={linkFactory}
          maxContentWidth={SHARED_TOPBAR_MAX_CONTENT_WIDTHS_DICTIONARY[maxContentWidth]}
          isHeaderFlush={isHeaderFlush}
        />
      )}
      <Injector
        marginTopSize={MARGIN_TOP_SIZES.EXTRA_LARGE}
        maxContentWidth={maxContentWidth}
        proviso={provisos.customComponents}
      >
        {children}
      </Injector>
      {showFooter && (
        <DynamicRendering height={footerHeight}>
          <SharedCorporateFooter
            bgColor={FOOTER_BACKGROUND_COLORS_DICTIONARY[bgColor]}
            design={sharedCorporateFooterDesigns.fullWidth}
            linkFactory={linkFactory}
            {...footerProps}
          />
        </DynamicRendering>
      )}
    </div>
  )
}

LayoutBasicFullWidth.displayName = 'LayoutBasicFullWidth'
LayoutBasicFullWidth.propTypes = {
  bgColor: PropTypes.string,
  children: PropTypes.element,
  footerProps: PropTypes.shape({
    linkFactory: PropTypes.func.isRequired
  }),
  isFooterless: PropTypes.bool,
  isHeaderless: PropTypes.bool,
  isHeaderFlush: PropTypes.bool,
  maxContentWidth: PropTypes.string,
  topbarProps: PropTypes.shape({
    linkFactory: PropTypes.func.isRequired
  }),
  linkFactory: PropTypes.func,
  className: PropTypes.string
}

export default LayoutBasicFullWidth
